import { Link } from '@tanstack/react-router';
import {
  LayoutGrid,
  Phone,
  FileText,
  Users,
  Calendar,
  Settings,
} from 'lucide-react';
import { useUiStore } from '@/lib/stores/ui';

type NavEntry = {
  to: string;
  icon: React.ElementType;
  label: string;
  badge?: string;
  exact?: boolean;
};

const WORK_NAV: NavEntry[] = [
  { to: '/',         icon: LayoutGrid, label: 'Pregled',  exact: true },
  { to: '/klici',    icon: Phone,      label: 'Klici',    badge: '3' },
  { to: '/ponudbe',  icon: FileText,   label: 'Ponudbe' },
  { to: '/stranke',  icon: Users,      label: 'Stranke' },
  { to: '/koledar',  icon: Calendar,   label: 'Koledar' },
];

const SETTINGS_NAV: NavEntry[] = [
  { to: '/nastavitve', icon: Settings, label: 'Nastavitve' },
];

export function Sidebar() {
  const { sidebarOpen, closeSidebar, agentActive, setAgentActive } = useUiStore();

  return (
    <aside className={`app-sidebar${sidebarOpen ? ' open' : ''}`}>
      <SidebarBrand />

      <NavGroup label="Delo" items={WORK_NAV} onNav={closeSidebar} />
      <NavGroup label="Nastavitve" items={SETTINGS_NAV} onNav={closeSidebar} />

      <div style={{ flex: 1 }} />

      <AgentCard active={agentActive} onToggle={setAgentActive} />
      <SidebarUser />
    </aside>
  );
}

/* ---- Sub-components ---- */

function SidebarBrand() {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '0.6rem',
      padding: '1.3rem 1.25rem',
      fontFamily: 'var(--font-display)',
      fontWeight: 600,
      fontSize: '1.25rem',
      color: '#fff',
      letterSpacing: '-0.02em',
      borderBottom: '1px solid var(--app-line)',
      flexShrink: 0,
    }}>
      <BrandMark />
      TradeCraft
    </div>
  );
}

function NavGroup({ label, items, onNav }: { label: string; items: NavEntry[]; onNav: () => void }) {
  return (
    <div style={{ padding: '1rem 0.75rem' }}>
      <div style={{
        fontFamily: 'var(--font-mono)',
        fontSize: '0.62rem',
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: 'var(--app-subtle)',
        padding: '0 0.5rem 0.5rem',
      }}>{label}</div>

      {items.map((item) => (
        <Link
          key={item.to}
          to={item.to}
          className="nav-item"
          activeProps={{ className: 'active' }}
          activeOptions={{ exact: !!item.exact }}
          onClick={onNav}
        >
          <item.icon size={18} strokeWidth={1.6} />
          {item.label}
          {item.badge && <span className="nav-badge">{item.badge}</span>}
        </Link>
      ))}
    </div>
  );
}

function AgentCard({ active, onToggle }: { active: boolean; onToggle: (v: boolean) => void }) {
  return (
    <div style={{
      margin: '0.75rem',
      padding: '0.85rem',
      background: 'linear-gradient(165deg,#241F1A,#1A1611)',
      border: '1px solid var(--app-line2)',
      borderRadius: '6px',
      flexShrink: 0,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', fontSize: '0.8rem', fontWeight: 540, color: '#fff' }}>
          <span style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            background: active ? '#4ADE80' : '#79716B',
            boxShadow: active ? '0 0 8px rgba(74,222,128,0.6)' : 'none',
            transition: `background var(--dur-base), box-shadow var(--dur-base)`,
            flexShrink: 0,
          }} />
          Agent {active ? 'aktiven' : 'na pavzi'}
        </span>
        <label className="toggle-switch">
          <input
            type="checkbox"
            checked={active}
            onChange={(e) => onToggle(e.target.checked)}
          />
          <span className="toggle-track" />
          <span className="toggle-thumb" />
        </label>
      </div>
      <div style={{
        fontFamily: 'var(--font-mono)',
        fontSize: '0.66rem',
        color: 'var(--app-muted)',
        marginTop: '0.4rem',
        letterSpacing: '0.02em',
      }}>
        {active ? 'Sprejema klice · od 07:00 do 18:00' : 'Pavza · klici gredo na odzivnik'}
      </div>
    </div>
  );
}

function SidebarUser() {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '0.6rem',
      padding: '0.9rem 1rem',
      borderTop: '1px solid var(--app-line)',
      flexShrink: 0,
    }}>
      <span style={{
        width: '32px',
        height: '32px',
        borderRadius: '50%',
        background: 'var(--copper-700)',
        color: '#fff',
        display: 'grid',
        placeItems: 'center',
        fontFamily: 'var(--font-mono)',
        fontSize: '0.7rem',
        fontWeight: 600,
        flexShrink: 0,
      }}>MN</span>
      <div>
        <div style={{ fontSize: '0.82rem', fontWeight: 540, color: 'var(--app-strong)' }}>Marko Novak</div>
        <div style={{ fontSize: '0.68rem', color: 'var(--app-subtle)', fontFamily: 'var(--font-mono)' }}>Inštalacije Novak</div>
      </div>
    </div>
  );
}

function BrandMark() {
  return (
    <svg viewBox="0 0 26 26" fill="none" width={24} height={24} aria-hidden="true" style={{ flexShrink: 0 }}>
      <rect x="0.5" y="0.5" width="25" height="25" rx="5" fill="#fff" fillOpacity="0.06" />
      <rect x="7"    y="13" width="2.4" height="6"  rx="1" fill="#B45309" />
      <rect x="11.8" y="9"  width="2.4" height="10" rx="1" fill="#E2A56B" />
      <rect x="16.6" y="11" width="2.4" height="8"  rx="1" fill="#B45309" />
    </svg>
  );
}
